import type { TrackingGroupShipment } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingGroupShipment";
import { LogoBackdrop } from "@freightsimple/ui-core/Display/LogoBackdrop";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { colorForShipmentState } from "@/Helpers/colorForShipmentState";
import { describeDateRelative } from "@/Helpers/describeDateRelative";
import { nameForShipmentState } from "@/Helpers/nameForShipmentState";
import { timeLabelForShipmentStateVerbose } from "@/Helpers/timeLabelForShipmentState";
import ShipmentSmallFromTo from "@/Shared/Shipment/CustomComponents/ShipmentSmallFromTo";
import { ShipmentRowTimeValue } from "@/Shared/Shipment/CustomComponents/ShipmentRowTimeValue";
import { makeTimeDescription } from "@/Shared/Shipment/Bars/Internal/shipmentTopBarParts";
import { useWidth } from "@/Shared/Hooks/useWidth";
import moment from "moment";

function describeCityFor(location: TrackingGroupShipment["pickupLocation"]) {
  return [location.address.city, location.address.stateOrProvinceCode]
    .filter((part) => Boolean(part))
    .join(", ");
}

function describeTimeFor(shipment: TrackingGroupShipment) {
  const { actualDeliveryDate, expectedDeliveryDate, pickupDate } = shipment;

  return makeTimeDescription(
    shipment.shipmentState,
    pickupDate === undefined ? "Unknown" : describeDateRelative(pickupDate),
    expectedDeliveryDate === undefined
      ? undefined
      : describeDateRelative(expectedDeliveryDate),
    undefined,
    actualDeliveryDate === undefined
      ? undefined
      : describeDateRelative(actualDeliveryDate),
  );
}

interface TrackingShipmentCardProps {
  shipment: TrackingGroupShipment;
  onClick: () => void;
  onMouseEnter?: (() => void) | undefined;
  onMouseLeave?: (() => void) | undefined;
}

export function TrackingShipmentCard(props: TrackingShipmentCardProps) {
  const { shipment, onClick } = props;
  const {
    billOfLadingNumber,
    carrierIdentifier,
    deliveryLocation,
    mostRecentTrackingLine,
    pickupLocation,
    proNumber,
    shipmentState,
  } = shipment;

  const stateStripeStyle = {
    backgroundColor: colorForShipmentState(shipmentState),
  };

  const [cardRef, cardWidth] = useWidth();
  const narrow = cardWidth > 0 && cardWidth < 640;

  return (
    <div
      ref={cardRef}
      className={`relative inline-block w-full max-w-[1024px] cursor-pointer rounded-[3px] border border-border-strong bg-card p-0 hover:border-border ${
        narrow ? "dashboard-row-narrow" : ""
      }`}
      onClick={onClick}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      role="button"
      tabIndex={0}
      data-testid="tracking-shipment-card"
      onKeyDown={function (event) {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div
        className="absolute top-0 left-0 h-full w-4"
        style={stateStripeStyle}
      />

      <div className="flex min-h-[72px] flex-row items-center in-[.dashboard-row-narrow]:flex-col in-[.dashboard-row-narrow]:items-start in-[.dashboard-row-narrow]:gap-2 in-[.dashboard-row-narrow]:py-4 in-[.dashboard-row-narrow]:pl-8">
        <div className="flex w-[150px] flex-col flex-nowrap items-start pl-8 in-[.dashboard-row-narrow]:w-auto in-[.dashboard-row-narrow]:pl-0">
          <div
            className="text-[16px] font-medium text-foreground"
            data-testid="tracking-card-state"
          >
            {nameForShipmentState(shipmentState)}
          </div>
          {billOfLadingNumber !== undefined && (
            <div className="text-[12px] font-semibold text-content-muted">
              BOL {billOfLadingNumber}
            </div>
          )}
          {proNumber !== undefined && (
            <div className="text-[11px] font-normal text-content-muted">
              PRO {proNumber}
            </div>
          )}
        </div>

        <ShipmentSmallFromTo
          pickupBusinessName={pickupLocation.businessName}
          pickupCity={describeCityFor(pickupLocation)}
          pickupReference=""
          deliveryBusinessName={deliveryLocation.businessName}
          deliveryCity={describeCityFor(deliveryLocation)}
          deliveryReference=""
        />

        {/* Same geometry as ShipmentRowTemplate's logo column, and for the same
         * reasons: room either side of the mark, and wide enough for the pad
         * that sits behind it in dark mode.
         *
         * It inherits the same floor, too. This 160px (mx-4 + w-[128px]) plus
         * ShipmentSmallFromTo's min-w-[280px] is 440px that cannot give way, so
         * the one-line layout needs about 676px while `narrow` above only
         * stacks below 640. A card between those two widths can run past its
         * own right edge. Left alone on purpose — see the
         * JustAboveTheStackingThreshold story on ShipmentRowTemplate, which
         * documents the floor for both surfaces. Every logo gets the same box,
         * left-aligned, with the logo centred inside it. */}
        <div className="mx-4 flex w-[128px] shrink-0 flex-row items-center justify-start in-[.dashboard-row-narrow]:mx-0 in-[.dashboard-row-narrow]:w-auto">
          {carrierIdentifier !== undefined && (
            <LogoBackdrop>
              <span className="flex h-[34px] w-[108px] items-center justify-center">
                <img
                  className="max-h-full max-w-full"
                  alt={displayNameForCarrier(carrierIdentifier)}
                  src={`/carrierLogos/${carrierIdentifier}.png`}
                />
              </span>
            </LogoBackdrop>
          )}
        </div>

        <div className="pl-8 in-[.dashboard-row-narrow]:pl-0">
          <ShipmentRowTimeValue
            label={timeLabelForShipmentStateVerbose(shipmentState, undefined)}
          >
            {describeTimeFor(shipment)}
          </ShipmentRowTimeValue>
        </div>
      </div>

      {mostRecentTrackingLine !== undefined && (
        <div className="border-t border-border-strong py-3 pr-4 pl-8">
          <div className="flex flex-row flex-wrap items-baseline gap-2">
            <div className="text-[11px] font-light text-content-muted">
              {moment(mostRecentTrackingLine.timestamp).format("MMM D, h:mma")}
            </div>
            <div className="text-[12px] font-normal text-foreground">
              {mostRecentTrackingLine.message}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
